import React from "react";
import { View, Text, Image, StyleSheet } from "react-native";

import PremiumProgressBar from "./PremiumProgressBar";

interface LeaderboardEntryCardProps {
  name: string;
  avatar_url?: string;
  title?: string;
  level?: number;
  xp?: number;
  scripture?: string;
  scripture_ref?: string;
  rank: number;
  isUser?: boolean;
}

// Refined palette for premium look
const GOLD = "#FFD700";
const RICH_GOLD = "#F6C700";
const ORANGE = "#fcb045";
const RICH_ORANGE = "#FF8C42";
const DARK_BLUE = "#172447"; // deeper navy
const DARKER_BLUE_OVERLAY = "rgba(23,36,71,0.93)";

const LeaderboardEntryCard: React.FC<LeaderboardEntryCardProps> = ({
  name,
  avatar_url,
  title,
  level = 1,
  xp = 0,
  scripture,
  scripture_ref,
  rank,
  isUser = false
}) => {
  // Dynamic XP max calculation (example: base 100 + 60 per level)
  const xpMax = 100 + (level - 1) * 60;
  const progress = Math.min(xp / xpMax, 1);

  // Web-only hover effect for premium look
  const [hovered, setHovered] = React.useState(false);

  return (
    <View
      style={[
        styles.card,
        rank === 1 && styles.firstPlace,
        isUser && styles.userCard,
        hovered && styles.cardHovered
      ]}
      // @ts-expect-error Mouse handlers are used for web hover support.
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <View style={styles.avatarCol}>
        <Image
          source={avatar_url ? { uri: avatar_url } : require("../../../assets/avatar-default.png")}
          style={[styles.avatar, rank === 1 && styles.avatarGold]}
        />
        {rank === 1 ? (
          <Text style={styles.crown} accessibilityLabel="Crown">👑</Text>
        ) : (
          <Text style={styles.shield} accessibilityLabel="Shield">🛡️</Text>
        )}
      </View>
      <View style={styles.infoCol}>
        <Text style={styles.name}>{name}{isUser ? " (You)" : ""}</Text>
        <Text style={styles.title}>{title ? `${title} • ` : ""}Level {level}</Text>
        {scripture && (
          <Text style={styles.scripture}>
            <Text style={styles.italic}>{scripture}</Text>
            {scripture_ref ? ` — ${scripture_ref}` : ""}
          </Text>
        )}
        <View style={styles.xpRow}>
          <PremiumProgressBar
            progress={progress}
            height={12}
            gradientColors={[RICH_ORANGE, RICH_GOLD]}
            style={styles.xpBar}
          />
          <Text style={styles.xpText}>{xp} XP</Text>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: DARKER_BLUE_OVERLAY,
    borderRadius: 18,
    padding: 18,
    marginBottom: 20,

    // For web, you can add transition via a custom style or CSS
    // transitionProperty: 'box-shadow, transform, background-color',
    // transitionDuration: '0.18s'
  },
  // Web-only hover effect
  cardHovered: {
    transform: [{ scale: 1.03 }],
    backgroundColor: "rgba(246,199,0,0.08)"
  },
  firstPlace: {
    borderWidth: 2.5,
    borderColor: RICH_GOLD
    },
  userCard: {
    borderColor: ORANGE,
    borderWidth: 2,
    backgroundColor: "rgba(252,176,69,0.10)"
  },
  avatarCol: {
    alignItems: "center",
    marginRight: 16
  },
  avatar: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: "#e5e7eb"
  },
  avatarGold: {
    borderWidth: 3.5,
    borderColor: RICH_GOLD
    },
  crown: {
    fontSize: 22,
    marginTop: 4,
    color: GOLD,
    textAlign: "center"
  },
  shield: {
    fontSize: 20,
    marginTop: 4,
    color: ORANGE,
    textAlign: "center"
  },
  infoCol: {
    flex: 1
  },
  name: {
    color: "#fff",
    fontWeight: "bold",
    fontSize: 19,
    marginBottom: 2,
    letterSpacing: 0.1

  },
  title: {
    color: RICH_ORANGE,
    fontWeight: "600",
    fontSize: 15,
    marginBottom: 2,
    letterSpacing: 0.05
  },
  scripture: {
    color: "#ccc",
    fontStyle: "italic",
    fontSize: 13,
    marginBottom: 4
  },
  italic: {
    fontStyle: "italic"
  },
  xpRow: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 8
  },
  xpBar: {
    flex: 1,
    marginRight: 10
  },
  xpText: {
    color: RICH_GOLD,
    fontWeight: "bold",
    fontSize: 15,
    minWidth: 56,
    textAlign: "right"

  }
});

export default LeaderboardEntryCard;
